import {
  IProgrammingGanttRow,
  IProgrammingGanttSchedule,
  ProgrammingGantt,
} from "@/components/programming-gantt/programming-gantt";
import { ProgrammingGanttRow } from "@/components/programming-gantt/programming-gantt-row";
import { ProgrammingGanttRowSchedule } from "@/components/programming-gantt/programming-gantt-row-schedule";

export class ProgrammingGanttRowScheduleContextMenu<
  ROW extends IProgrammingGanttRow,
  SCHEDULE extends IProgrammingGanttSchedule,
> {
  private $container!: HTMLElement;

  private isOpened: boolean = false;

  constructor(
    private readonly gantt: ProgrammingGantt<ROW, SCHEDULE>,
    private readonly row: ProgrammingGanttRow<ROW, SCHEDULE>,
    private readonly schedule: ProgrammingGanttRowSchedule<ROW, SCHEDULE>,
  ) {}

  private mount() {
    const contextMenu = this.schedule.getRawSchedule().contextMenu;

    this.$container = document.createElement("div");
    this.$container.classList.add("gantt-row-schedule-context-menu");

    const inner = document.createElement("div");
    inner.classList.add("context-menu__inner");

    const list = document.createElement("ul");
    list.classList.add("context-menu__list");

    for (const contextMenuItem of contextMenu) {
      const element = document.createElement("li");

      if (contextMenuItem.type === "DIVIDER") {
        element.classList.add("context-menu__divider");
      } else if (contextMenuItem.type === "ACTION") {
        element.classList.add("context-menu__item");
        if (contextMenuItem.color) {
          element.style.color = contextMenuItem.color;
        }

        const content = document.createElement("div");
        content.classList.add("context-menu__item-content");

        if (contextMenuItem.icon) {
          const icon = document.createElement("i");
          icon.classList.add("pi");
          icon.classList.add(contextMenuItem.icon);
          icon.classList.add("context-menu__item-icon");
          content.appendChild(icon);
        }

        const label = document.createElement("span");
        label.classList.add("context-menu__item-label");
        label.innerHTML = contextMenuItem.label;
        content.appendChild(label);

        element.setAttribute("action-id", contextMenuItem.id);
        element.setAttribute("role", "button");
        element.addEventListener("click", () => {
          this.gantt.contextMenuItemClicked(this.row.getRow(), this.schedule.getRawSchedule(), contextMenuItem.id);
        });
        element.appendChild(content);
      }

      list.appendChild(element);
    }

    inner.appendChild(list);

    this.$container.appendChild(inner);
    document.documentElement.appendChild(this.$container);

    document.addEventListener("click", (e) => {
      if (e.target === this.$container) {
        return;
      }

      const button = e.which || e.button;
      if (button === 1) {
        this.toggleMenuOff();
      }
    });

    window.addEventListener("keyup", (e) => {
      if (e.keyCode === 27) {
        this.toggleMenuOff();
      }
    });
  }

  private unmount() {
    this.$container.remove();
  }

  public destroy() {
    this.toggleMenuOff();
  }

  public getContainer() {
    return this.$container;
  }

  public toggleMenuOn(event: MouseEvent) {
    const contextMenu = this.schedule.getRawSchedule().contextMenu;

    if (contextMenu.length === 0) {
      return;
    }

    if (this.isOpened) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.mount();
    this.isOpened = true;
    this.$container.classList.remove("closed");
    this.$container.classList.add("opened");
    this.positionMenu(event);
  }

  public toggleMenuOff() {
    if (!this.isOpened) {
      return;
    }

    this.isOpened = false;
    this.$container.classList.remove("opened");
    this.$container.classList.add("closed");
    this.unmount();
  }

  private getPosition(e: MouseEvent) {
    let posx = 0;
    let posy = 0;

    if (e.pageX || e.pageY) {
      posx = e.pageX;
      posy = e.pageY;
    } else if (e.clientX || e.clientY) {
      posx = e.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
      posy = e.clientY + document.body.scrollTop + document.documentElement.scrollTop;
    }

    return {
      x: posx,
      y: posy,
    };
  }

  public positionMenu(e: MouseEvent) {
    const clickCoords = this.getPosition(e);
    const clickCoordsX = clickCoords.x;
    const clickCoordsY = clickCoords.y;

    const menuWidth = this.$container.offsetWidth + 4;
    const menuHeight = this.$container.offsetHeight + 4;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    if (windowWidth - clickCoordsX < menuWidth) {
      this.$container.style.left = windowWidth - menuWidth + "px";
    } else {
      this.$container.style.left = clickCoordsX + "px";
    }

    if (windowHeight - clickCoordsY < menuHeight) {
      this.$container.style.top = windowHeight - menuHeight + "px";
    } else {
      this.$container.style.top = clickCoordsY + "px";
    }
  }
}
